<template>
    <div class="p-details w-full">
        <el-form
            :model="preExistingAccreditationForm"
            :rules="validationRules"
            ref="preExistingAccreditationForm"
            class="pd-form"
        >
            <h4 class="font-display font-semibold mt-8 pb-3">Pre-existing Accreditation</h4>
            <div
                v-if="!preExistingAccreditationForm.organisationPtrId"
                class="flex flex-row w-auto"
            >
                <el-form-item prop="isFirstAccreditation" class="mb-0">
                    <div class="inline-block align-middle text-sm">
                        <span>Is this the practice's first accreditation?</span>
                        <el-popover
                            placement="top-start"
                            title="First Accreditation"
                            width="200"
                            trigger="hover"
                            content="Has your practice or service previously undertaken accreditataion against the RACGP standards"
                        >
                            <fa-icon icon="question-circle" slot="reference" class="grey-1 ml-2"/>
                        </el-popover>
                    </div>
                    <el-radio-group v-model="preExistingAccreditationForm.isFirstAccreditation">
                        <el-radio :label="true" class="pn-radio" data-cy="practiceAccreditationYes" :disabled="readonly">Yes</el-radio>
                        <el-radio :label="false" class="pn-radio" data-cy="practiceAccreditationNo" :disabled="readonly">No</el-radio>
                    </el-radio-group>
                </el-form-item>
            </div>
            <div v-if="!preExistingAccreditationForm.organisationPtrId && !preExistingAccreditationForm.isFirstAccreditation" class="mt-1 w-full" data-cy="firstAccreditationSection">
                <v-form ref="firstAccreditation" v-model="isValid" class="grid grid-cols-4 gap-x-4 w-full">
                    <div class="col-span-1 grow-0">
                        <v-select
                            label="Current Provider"
                            class="currentProvider"
                            attach=".currentProvider"
                            :items="providerOptions"
                            v-model="preExistingAccreditationForm.previousProvider"
                            item-text="label"
                            item-value="value"
                            :rules="[required]"
                        ></v-select>
                    </div>
                    <div v-if="showPreviousProviderOther" class="col-span-1">
                        <v-text-field
                            v-model="preExistingAccreditationForm.previousProviderOther"
                            :readonly="readonly"
                            label="Provider name"
                        ></v-text-field>
                    </div>
                    <div class="col-span-1">
                        <div class="mt-1 datepickerFloat">
                            <EditableDatePicker
                                class="expiryDate" 
                                v-model="preExistingAccreditationForm.previousExpiryDate"
                                label="Expiry Date"
                                :rules="[required]"
                            />
                        </div>
                    </div>
                    <div class="col-span-1">
                        <v-select
                            class="previousStandard"
                            :items="previousStandards"
                            attach=".previousStandard"
                            label="Standard certified to"
                            v-model="preExistingAccreditationForm.previousStandard"
                            :rules="[required]"
                        ></v-select>
                    </div>

                    <div v-if="!preExistingAccreditationForm.organisationPtrId" class="col-span-3">
                        <v-file-input
                            label="Certificate File (PDF, JPG, PNG)"
                            truncate-length="15"
                            accept="image/jpeg,image/png,application/pdf,image/gif"
                            :rules="[required]"
                            @change="handlePreviousCertificateChange"
                        ></v-file-input>
                    </div>
                </v-form>
            </div>
            
            <div class="flex flex-row w-auto">
                <el-form-item prop="isReceivingIncentives" class="mb-2">
                    <div class="inline-block align-middle text-sm">
                        <span>Is the practice currently receiving incentive payments?</span>
                        <el-popover
                            placement="top-start"
                            title="Incentive payments"
                            width="200"
                            trigger="hover"
                            content="Incentive payments include (PIP) or Practice Incentive Payments from Services Australia as an accredited general practice."
                        >
                            <fa-icon icon="question-circle" slot="reference" class="grey-1 ml-2"/>
                        </el-popover>
                    </div>
                    <el-radio-group v-model="preExistingAccreditationForm.isReceivingIncentives">
                        <el-radio :label="true" class="pn-radio" :disabled="readonly">Yes</el-radio>
                        <el-radio :label="false" class="pn-radio" :disabled="readonly">No</el-radio>
                    </el-radio-group>
                </el-form-item>
            </div>
            <div class="flex flex-row w-auto">
                <div class="grid grid-cols-2 gap-10" v-if="preExistingAccreditationForm.isReceivingIncentives">
                    <div class="grid grid-cols-2 gap-2">
                        <div>
                            <el-form-item prop="initialSwpe" class="mb-2">
                                <label class="block font-bold font-display text-xs text-grey-2 uppercase leading-tight">SWPE</label>
                                <el-input v-model="preExistingAccreditationForm.initialSwpe" :readonly="readonly"></el-input>
                            </el-form-item>
                        </div>
                        <div>
                            <el-form-item prop="pipId" class="mb-2">
                                <label
                                    class="block font-bold font-display text-xs text-grey-2 uppercase leading-tight"
                                >PIP ID</label>
                                <el-input v-model="preExistingAccreditationForm.pipId" :readonly="readonly"></el-input>
                            </el-form-item>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="!isEditable || isReapplying" class="flex flex-row w-auto">
                <h4 class="font-display font-semibold mt-8 pb-3">Survey Version Edition</h4>
                <el-popover
                    placement="top-start"
                    title="Survey Version"
                    width="400"
                    class="mt-9 pb-3"
                    trigger="hover"
                    v-if="editionStandardsText"
                >
                    <div v-html="editionStandardsText"></div>
                    <fa-icon icon="question-circle" slot="reference" class="grey-1 ml-2"/>
                </el-popover>
            </div>
            <div v-if="!isEditable || isReapplying" class="flex flex-row w-auto">
                <v-form ref="accreditationStandards" v-model="isValid" class="grid grid-cols-4 gap-x-4 w-full">
                    <div class="col-span-1 grow-0">
                        <v-select
                            label="Edition"
                            class="surveyVersionEditionNumber"
                            attach=".surveyVersionEditionNumber"
                            :items="editionNumbers"
                            v-model="preExistingAccreditationForm.surveyVersionEditionNumber"
                            item-text="label"
                            item-value="value"
                            :rules="[required]"
                            :disabled="editionNumbers.length <= 1"
                        ></v-select>
                    </div>
                    <div class="col-span-1 flex flex-row items-center align-center justify-between">
                        <v-select
                            class="surveyVersionSubtype"
                            :items="versionSubtypes"
                            attach=".surveyVersionSubtype"
                            label="Sub type"
                            item-text="label"
                            item-value="value"
                            v-model="preExistingAccreditationForm.surveyVersionSubtype"
                            :rules="[required]"
                            :disabled="versionSubtypes.length <= 1"
                        ></v-select>
                    </div>
                </v-form>
            </div>
        </el-form>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import { computed, toRefs, reactive, watch } from '@vue/composition-api'
import FileMethods from '@/components/shared/mixins/fileMethods'
import {
    PREV_ACCRED_FOLDER_CODE,
    BASE_PRACTICE_SUB_TYPE,
    DEFAULT_SURVEY_VERSION_EDITION_NUMBER,
    practiceSubTypeMapping,
    SURVEY_VERSION_NUMBERS
} from '@/config/constants'
import { isFeatureEnabled } from '@/services/featureFlags'
import FolderMixin from '@/components/shared/mixins/folderMixin'
import EditableDatePicker from '@/components/partials/EditableDatePicker'
import { FormRules } from '@/components/shared/mixins/formMixins'
import { toTitleCase } from '@/utils/humanReadable'

export default {
    name: 'pre-existing-accreditation',
    components: {EditableDatePicker},
    mixins: [FileMethods, FolderMixin, FormRules],
    props: {
        value: {
            type: Object,
            default: () => {
            }
        },
        isReapplying: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        availableSurveyVersions: {
            type: Object,
            default: () => ({})
        },
        isEditable: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            dateFormat: 'dd/MM/yyyy',
            validationRules: {
                isFirstAccreditation: [{ required: true, message: 'This field is required', trigger: 'change' }],
                isReceivingIncentives: [{ required: true, message: 'This field is required', trigger: 'change' }],
                previousProviderOther: [{ required: true, message: 'This field is required', trigger: 'change' }],
            },
            providerOptions: [{
                value: 'AGPAL',
                label: 'AGPAL'
            }, {
                value: 'QPA',
                label: 'QPA'
            }, {
                value: 'OTHER',
                label: 'Other'
            }],
            documentFolderId: 0,
            getVisibleFolders: true,
            practiceId: null,
            emptyHidden: true,
            isValid: true
        }
    },
    beforeMount() {
        this.loadDocumentFolderCode()
    },
    mounted() {
        if (!this.value.organisationPtrId) {
            this.validationRules.previousCertificate = [{ required: true, message: 'This field is required', trigger: 'blur' }]
        }
    },
    methods: {
        validate() {
            if (this.$refs.firstAccreditation && !this.validateFirstAccredForm()) return false

            return this.$refs.preExistingAccreditationForm.validate()
        },
        validateFirstAccredForm() {
            if (this.preExistingAccreditationForm.isFirstAccreditation) return this.isValid = true
            return this.isValid = this.$refs.firstAccreditation.validate()
        },
        handlePreviousCertificateChange(file) {
            console.log('file uploaded', file)
            this.preExistingAccreditationForm.previousCertificate = file
            this.preExistingAccreditationForm.previousCertificateFolderId = file ? this.documentFolderId : null

            this.$emit('input', this.preExistingAccreditationForm)
        },
        handlePreviousCertificateRemove(file) {
            this.preExistingAccreditationForm.previousCertificate = null
            this.$emit('input', this.preExistingAccreditationForm)
        },
        async loadDocumentFolderCode() {
            const prevAccredFolder = await this.getFolder(PREV_ACCRED_FOLDER_CODE)
            if (prevAccredFolder) this.documentFolderId = prevAccredFolder.id
        }
    },
    computed: {
        ...mapState({
            staticChoices: state => state.app.staticChoices,
            folders: state => state.app.folders,
        }),
        previousStandards() {
            return this.staticChoices['practice.previous_standard'] || []
        },
        showFirstAccordion() {
            return !this.isReapplying &&
                !this.preExistingAccreditationForm?.hasMultipleAccreditations &&
                !this.preExistingAccreditationForm?.isFirstAccreditation
        },
        showPreviousProviderOther() {
            return this.preExistingAccreditationForm.previousProvider &&
                this.preExistingAccreditationForm.previousProvider.toLowerCase() === 'other'
        }
    },
    setup(props, { emit }) {
        const state = reactive({
            editionNumbers: [],
            // versionSubtypes: [],
            // availableSurveyVersions: []
        })
        const preExistingAccreditationForm = computed({
            get() {
                return props.value || {}
            },
            set(value) {
                emit('input', value)
            }
        })
        // Scope 3.2: first-time applicants get 6th Base only. Answering "no",
        // and reapplications, may choose 5th, 6th Base or 6th PoCT.
        const surveyVersionKey = computed(() => {
            if (props.isReapplying || props.value.isFirstAccreditation === false) {
                return 'accredited'
            }
            return 'new'
        })
        const isEditable = computed(() => props.isEditable)
        const isReapplying = computed(() => props.isReapplying)
        const localAvailableSurveyVersions = computed(() => props.availableSurveyVersions || {})
        const practice = computed(() => props.value || {})
        const editionStandardsText = computed(() => {
            if (!isFeatureEnabled('sixthEdition')) return ''
            return `
<div class="text-sm">
  <p class="font-semibold">6th Edition Standards - Base - general practice and aboriginal health services</p>
  <p class="mt-2">The assessment includes the assessment of four standards which include:</p>
  <ul class="list-disc pl-5 mt-1">
    <li>F for Foundations of general practice</li>
    <li>CG for Clinical governance</li>
    <li>PP for Patient participation</li>
    <li>CQI for Continuous quality improvement</li>
  </ul>
  <p class="font-semibold mt-4">6th Edition Standards - PoCT - including Point of Care Testing</p>
  <p class="mt-2">The assessment includes the assessment of four standards which include:</p>
  <ul class="list-disc pl-5 mt-1">
    <li>F for Foundations of general practice</li>
    <li>CG for Clinical governance</li>
    <li>PP for Patient participation</li>
    <li>CQI for Continuous quality improvement</li>
    <li>PoCT for Point-of-care testing</li>
  </ul>
  <p class="italic mt-4">Standards with PoCT</p>
  <p class="mt-2">Point-of-care testing (PoCT) is defined as testing performed outside the laboratory environment, near to or at the side of the patient, which is not done under the supervision of a trained laboratory professional.</p>
  <p class="mt-2">To be accredited against the point-of-care (PoCT) standard, a practice needs to:</p>
  <ul class="list-disc pl-5 mt-1">
    <li>meet all required criteria in the PoCT standard in addition to all other criteria in the Standards (The RACGP encourages practices to meet aspirational criteria, but they are not essential)</li>
    <li>be formally assessed against the PoCT standard by an accrediting agency approved under the National General Practice Accreditation Scheme (the Scheme)</li>
  </ul>
</div>
            `
        })
        // Versions on offer for this practice type and accreditation path.
        // With the 6th edition flag off they are hidden everywhere.
        const availableVersionsForPractice = computed(() => {
            const versions = localAvailableSurveyVersions.value?.[surveyVersionKey.value]?.[practice.value.practiceType?.toLowerCase?.()] || []
            if (isFeatureEnabled('sixthEdition')) return versions
            // Keep the edition the practice is already recorded against, so
            // turning the flag off never blanks out an existing selection.
            const selected = preExistingAccreditationForm.value.surveyVersionEditionNumber
            return versions.filter(v => v.editionNumber !== SURVEY_VERSION_NUMBERS.SIXTH_EDITION || v.editionNumber === selected)
        })
        const versionsForEdition = (editionNumber) =>
            availableVersionsForPractice.value.filter(v => v.editionNumber === editionNumber)
        // Base is the default wherever it is offered. PoCT is opt-in, so it is
        // never selected on the practice's behalf.
        const defaultSubtypeForEdition = (editionNumber) => {
            const versions = versionsForEdition(editionNumber)
            const base = versions.find(v => v.versionSubtype === BASE_PRACTICE_SUB_TYPE)
            return (base || versions[0])?.versionSubtype ?? BASE_PRACTICE_SUB_TYPE
        }
        const versionSubtypes = computed(() => {
            // need to filter version subtypes based on selected edition number
            if (!preExistingAccreditationForm.value.surveyVersionEditionNumber) return []
            const subtypes = versionsForEdition(preExistingAccreditationForm.value.surveyVersionEditionNumber).map(v => {
                const keys = new Set(Object.keys(practiceSubTypeMapping))
                const label = keys.has(v.versionSubtype)
                    ? practiceSubTypeMapping[v.versionSubtype]
                    : toTitleCase(v.versionSubtype)
                return { value: v.versionSubtype, label }
            })
            // Matches the base-only default the watcher falls back to when the
            // BE offers nothing for this edition.
            if (!subtypes.length) return [{ value: BASE_PRACTICE_SUB_TYPE, label: practiceSubTypeMapping[BASE_PRACTICE_SUB_TYPE] }]
            return subtypes
        })
        // TODO: maybe 
        const practiceType = computed(() => practice.value.practiceType?.toLowerCase?.())
        const isFirstAccreditation = computed(() => practice.value.isFirstAccreditation)
        watch([isFirstAccreditation, practiceType, isReapplying, isEditable, localAvailableSurveyVersions], ([newIsFirstAccreditation, _, newIsReapplying, newIsEditable]) => {
            const availableVersions = availableVersionsForPractice.value
            const canAutoSelect = !newIsEditable && !newIsReapplying
            if (availableVersions.length > 0) {
                // One entry per edition - an edition offering both Base and
                // PoCT is two versions but still a single edition choice.
                state.editionNumbers = [...new Set(availableVersions.map(v => v.editionNumber))]
                    .sort((a, b) => b - a)
                    .map(editionNumber => ({ value: editionNumber, label: `${editionNumber}th edition` }))
                const latestEditionNumber = Math.max(...availableVersions.map(v => v.editionNumber))
                if (canAutoSelect) {
                    preExistingAccreditationForm.value.surveyVersionEditionNumber = latestEditionNumber
                }
                const selectedEditionNumber = preExistingAccreditationForm.value.surveyVersionEditionNumber
                if (selectedEditionNumber && canAutoSelect) {
                    preExistingAccreditationForm.value.surveyVersionSubtype = defaultSubtypeForEdition(selectedEditionNumber)
                }
            } else {
                state.editionNumbers = [{ value: DEFAULT_SURVEY_VERSION_EDITION_NUMBER, label: `${DEFAULT_SURVEY_VERSION_EDITION_NUMBER}th edition` }]
                if (canAutoSelect) {
                    preExistingAccreditationForm.value.surveyVersionEditionNumber = DEFAULT_SURVEY_VERSION_EDITION_NUMBER
                    preExistingAccreditationForm.value.surveyVersionSubtype = BASE_PRACTICE_SUB_TYPE
                }
            }
        }, { immediate: true, deep: true })
        // Switching edition can leave a subtype the new edition does not offer
        // (5th edition has no PoCT); fall back rather than leaving it blank.
        watch(() => preExistingAccreditationForm.value.surveyVersionEditionNumber, (editionNumber) => {
            if (!editionNumber) return
            const options = versionSubtypes.value.map(option => option.value)
            if (!options.length || options.includes(preExistingAccreditationForm.value.surveyVersionSubtype)) return
            preExistingAccreditationForm.value.surveyVersionSubtype = defaultSubtypeForEdition(editionNumber)
        })
        return {
            ...toRefs(state),
            editionStandardsText,
            versionSubtypes,
            preExistingAccreditationForm,
            isFirstAccreditation
        }
    }
}
</script>
